import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Box,
    CircularProgress,
    Typography,
    AppBar,
    Toolbar,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RotateRight as RotateIcon,
    Download as DownloadIcon,
    Fullscreen as FullscreenIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import './ImageViewer.css';
import authService from '../services/auth.service';

interface ImageViewerProps {
    open: boolean;
    onClose: () => void;
    documentId: string;
    fileName: string;
    fileSize?: number;
    metadata?: any;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
    open,
    onClose,
    documentId,
    fileName,
    fileSize,
    metadata
}) => {
    const [imageUrl, setImageUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rotation, setRotation] = useState(0);
    const [showInfo, setShowInfo] = useState(false);

    useEffect(() => {
        if (open && documentId) {
            loadImage();
        }
        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [open, documentId]);

    const loadImage = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const api = authService.getApiInstance();
            const response = await api.get(`/documents/${documentId}/preview`, {
                responseType: 'blob'
            });
            
            const url = URL.createObjectURL(response.data);
            setImageUrl(url);
        } catch (err) {
            console.error('Failed to load image:', err);
            setError('Failed to load image');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.get(`/documents/${documentId}/download`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const handleRotate = () => {
        setRotation((prev) => (prev + 90) % 360);
    };

    const formatFileSize = (bytes?: number): string => {
        if (!bytes) return 'Unknown size';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    width: '90vw',
                    maxWidth: 'none',
                    bgcolor: 'background.default'
                }
            }}
        >
            <AppBar position="relative" color="default" elevation={1}>
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {fileName}
                    </Typography>
                    <Tooltip title="Info">
                        <IconButton onClick={() => setShowInfo(!showInfo)} color="inherit">
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Rotate">
                        <IconButton onClick={handleRotate} color="inherit">
                            <RotateIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                        <IconButton onClick={handleDownload} color="inherit">
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton onClick={onClose} color="inherit">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ 
                p: 0, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                bgcolor: 'grey.100',
                position: 'relative'
            }}>
                {loading && (
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Loading image...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography variant="h6" color="error">
                            {error}
                        </Typography>
                    </Box>
                )}

                {!loading && !error && imageUrl && (
                    <Box
                        sx={{
                            transform: `rotate(${rotation}deg)`,
                            transition: 'transform 0.3s ease',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}
                    >
                        <Zoom>
                            <img
                                src={imageUrl}
                                alt={fileName}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </Zoom>
                    </Box>
                )}

                {showInfo && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            bgcolor: 'background.paper',
                            p: 2,
                            borderRadius: 1,
                            boxShadow: 2,
                            minWidth: 250
                        }}
                    >
                        <Typography variant="subtitle2" gutterBottom>
                            File Information
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Name: {fileName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Size: {formatFileSize(fileSize)}
                        </Typography>
                        {metadata?.dimensions && (
                            <Typography variant="body2" color="text.secondary">
                                Dimensions: {metadata.dimensions.width} × {metadata.dimensions.height}
                            </Typography>
                        )}
                        {metadata?.format && (
                            <Typography variant="body2" color="text.secondary">
                                Format: {metadata.format}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ImageViewer;